import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, RowClassParams } from 'ag-grid-enterprise';
import { useAtomValue } from 'jotai';
import { createCSVTableColumnDefs } from './CSVTableColumnDefs';
import { createAgTheme } from '~/components/ui/tables/ag/agTheme';
import { siteThemeMode } from '~/atoms/userPreferencesAtoms';

interface CSVAllocationData {
  isin: string;
  current_allocation: number;
  sector: string;
  ticker?: string;
  name?: string;
  _hasError?: boolean;
  _errorMessage?: string;
}

interface CSVTableProps {
  data: CSVAllocationData[];
  loading?: boolean;
  height?: string;
  onDataChange?: (updatedData: CSVAllocationData[]) => void;
}

export const CSVTable: React.FC<CSVTableProps> = ({
  data,
  loading = false,
  height = '400px',
  onDataChange,
}) => {
  const [colDefs, setColDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<CSVAllocationData[]>(data);
  const gridRef = useRef<AgGridReact<CSVAllocationData>>(null);
  const themeMode = useAtomValue(siteThemeMode);
  const gridTheme = useMemo(() => createAgTheme(themeMode), [themeMode]);
  const isUpdatingFromExternal = useRef(false);

  useEffect(() => {
    if (data && data.length > 0) {
      isUpdatingFromExternal.current = true;
      const newColDefs = createCSVTableColumnDefs(data);
      setColDefs(newColDefs);
      setRowData(data);

      const timeoutId = setTimeout(() => {
        if (gridRef.current?.api) {
          gridRef.current.api.sizeColumnsToFit();
        }
        isUpdatingFromExternal.current = false;
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        isUpdatingFromExternal.current = false;
      };
    } else {
      isUpdatingFromExternal.current = true;
      setRowData([]);
      isUpdatingFromExternal.current = false;
    }
  }, [data]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCellValueChanged = (params: any) => {
    if (params.colDef.field === 'sector') {
      const newValue = params.newValue?.trim().toLowerCase();
      if (newValue !== 'c' && newValue !== 'd' && newValue !== '') {
        params.api.refreshCells({
          force: true,
          rowNodes: [params.node],
          columns: [params.column.getId()],
        });
        return;
      }
      params.data.sector = newValue;
    }

    const updatedData = [...rowData];
    const rowIndex = params.rowIndex;
    if (updatedData[rowIndex]) {
      updatedData[rowIndex] = { ...updatedData[rowIndex], ...params.data };
      // This is a user edit, so we don't set the flag - onDataChange should be called
      setRowData(updatedData);
    }
  };

  const getRowClass = (
    params: RowClassParams<CSVAllocationData>,
  ): string | string[] => {
    if (params.data?._hasError) {
      return 'ag-row-error';
    }
    return '';
  };

  useEffect(() => {
    // Only call onDataChange if the change came from user edits, not external data updates
    if (!isUpdatingFromExternal.current && rowData !== data && onDataChange) {
      onDataChange(rowData);
    }
  }, [rowData, data, onDataChange]);

  if (data.length === 0) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height }}
      >
        <div className="text-gray-500">No data to display</div>
      </div>
    );
  }

  return (
    <div
      style={{ height, width: '100%' }}
      className="w-full csv-table-container"
    >
      <style>{`
        .csv-table-container .ag-row-error {
          background-color: rgba(239, 68, 68, 0.15) !important;
          border-left: 3px solid #ef4444 !important;
        }
        .csv-table-container .ag-row-error:hover {
          background-color: rgba(239, 68, 68, 0.25) !important;
        }
      `}</style>
      <AgGridReact
        rowData={rowData}
        ref={gridRef}
        loading={loading}
        columnDefs={colDefs}
        theme={gridTheme}
        suppressContextMenu
        colResizeDefault="shift"
        suppressCellFocus
        getRowClass={getRowClass}
        defaultColDef={{
          resizable: true,
          suppressSizeToFit: false,
        }}
        onGridReady={() => {
          if (gridRef.current?.api) {
            gridRef.current.api.sizeColumnsToFit();
          }
        }}
        onGridSizeChanged={() => {
          if (gridRef.current?.api) {
            gridRef.current.api.sizeColumnsToFit();
          }
        }}
        onFirstDataRendered={() => {
          if (gridRef.current?.api) {
            gridRef.current.api.sizeColumnsToFit();
          }
        }}
        autoSizeStrategy={{
          type: 'fitGridWidth',
          defaultMinWidth: 100,
        }}
        domLayout="normal"
        suppressHorizontalScroll={false}
        rowHeight={40}
        headerHeight={50}
        onCellValueChanged={onCellValueChanged}
        tooltipShowDelay={500}
      />
    </div>
  );
};
